import { Box, Link, Stack, Typography } from '@mui/material';
import { ActivityLogItem } from '@karehero/models';
import { formatRelativeTime } from '@karehero/helpers/format';
import { Icon } from '@/components/atoms/Icon';
import { Icons } from '@/components/atoms/Icon/icons';

const activityTypeIcon: Record<string, Icons> = {
  document_scanned: 'file-lines',
  action_completed: 'check-circle',
  shortlist_item_secured: 'magnifying-glass',
  form_completed: 'clipboard-check',
};

const CaringForCaption = ({
  name,
  careCircleId,
  onCareCircleClick,
}: {
  name: string;
  careCircleId?: string;
  onCareCircleClick?: (careCircleId: string) => void;
}) => (
  <Typography
    variant='caption'
    sx={{
      color: 'text.secondary',
    }}
  >
    Caring for{' '}
    {careCircleId && onCareCircleClick ? (
      <Link
        component='button'
        variant='caption'
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onCareCircleClick(careCircleId);
        }}
        sx={{ verticalAlign: 'baseline' }}
      >
        {name}
      </Link>
    ) : (
      name
    )}
  </Typography>
);

export interface ActivityRowProps {
  item: ActivityLogItem;
  href?: string;
  onClick?: () => void;
  onCareCircleClick?: (careCircleId: string) => void;
}

export const ActivityRow = ({
  item,
  href,
  onClick,
  onCareCircleClick,
}: ActivityRowProps) => {
  const content = (
    <Stack
      direction='row'
      spacing={1.5}
      sx={{
        alignItems: 'center',
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Icon
        icon={activityTypeIcon[item.activityType] ?? 'circle'}
        fontSize={18}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography variant='body2'>
          {item.actorName} {item.title}
        </Typography>
        {item.careRecipientName && (
          <CaringForCaption
            name={item.careRecipientName}
            careCircleId={item.careCircleId ?? undefined}
            onCareCircleClick={onCareCircleClick}
          />
        )}
      </Box>
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
          flexShrink: 0,
        }}
      >
        {formatRelativeTime(item.occurredAt)}
      </Typography>
    </Stack>
  );

  if (href || onClick) {
    return (
      <Link
        href={href}
        onClick={onClick}
        underline='none'
        color='inherit'
        sx={{ cursor: 'pointer' }}
      >
        {content}
      </Link>
    );
  }

  return content;
};

export default ActivityRow;
